import React from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LogOut, LayoutDashboard, Inbox, ClipboardList, Receipt, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";

const LINKS = [
  { to: "/admin/ops", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "ops-nav-dashboard" },
  { to: "/admin/ops/quotes", label: "Solicitudes", icon: Inbox, testid: "ops-nav-quotes" },
  { to: "/admin/ops/orders", label: "Pedidos", icon: ClipboardList, testid: "ops-nav-orders" },
  { to: "/admin/ops/invoices", label: "Facturas", icon: Receipt, testid: "ops-nav-invoices" },
];

export default function OpsLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-[#0E0E0E] border-r border-white/5 sticky top-0 h-screen">
        <div className="px-6 py-7 border-b border-white/5 flex items-center gap-3">
          <Logo size={40} variant="mark" />
          <div className="leading-tight">
            <div className="logo-mark text-white text-base">M&amp;C</div>
            <div className="logo-mark text-[10px] tracking-[0.3em] text-[#D4AF37]">CLEAN FRESH</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1">
          <Link
            to="/admin"
            data-testid="ops-back-to-hub"
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-white/55 hover:bg-white/[0.04] hover:text-[#D4AF37] mb-2"
          >
            ← Back to hub
          </Link>

          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
                  isActive ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30" : "text-white/65 hover:bg-white/[0.04] hover:text-white"
                }`
              }
            >
              <l.icon className="w-4 h-4" /> {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/5 px-4 py-4 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-white/65 hover:text-[#D4AF37] hover:bg-white/[0.04] transition-all"
          >
            <ExternalLink className="w-4 h-4" /> View live site
          </a>
          <button
            onClick={onLogout}
            data-testid="ops-logout"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-white/65 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          <div className="pt-2 text-[10px] text-white/30 truncate">{user?.email}</div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 px-4 flex items-center justify-between bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/5">
        <Link to="/admin" className="flex items-center gap-2">
          <Logo size={32} variant="mark" />
          <span className="logo-mark text-white text-sm">Admin</span>
        </Link>
        <button onClick={onLogout} className="text-xs text-white/70 hover:text-red-400 flex items-center gap-1.5">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="lg:hidden border-b border-white/5 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 px-4 py-3">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${isActive ? "bg-[#D4AF37] text-[#0A0A0A]" : "bg-white/5 text-white/70"}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>

        <Outlet />
      </main>
    </div>
  );
}
